import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Check, Sparkles, ChevronDown, ChevronRight, FileText, Database, Users, Building2, AlertCircle, CheckCircle2, XCircle, PlayCircle, Loader2, History, Trash2, Clock, RotateCcw, ArrowRight } from 'lucide-react';
import { api } from '../lib/api';
import { Case } from '../types';
import Button from '../components/ui/Button';
import Card, { CardHeader, CardBody, CardFooter } from '../components/ui/Card';
import Spinner from '../components/ui/Spinner';

export default function CaseSummary() {
  const navigate = useNavigate();
  const { caseId } = useParams<{ caseId: string }>();
  const queryClient = useQueryClient();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['all', 'analysis']));
  const [selectedAnalysisId, setSelectedAnalysisId] = useState<string | null>(null);
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    show: boolean;
    analysisId: string | null;
    position: { top: number; left: number } | null;
  }>({
    show: false,
    analysisId: null,
    position: null,
  });
  const [showRewriteReview, setShowRewriteReview] = useState(false);
  const [aiRewrittenData, setAiRewrittenData] = useState<{
    domain: string;
    context: string;
  } | null>(null);
  const [editedDomain, setEditedDomain] = useState('');
  const [editedContext, setEditedContext] = useState('');
  const [showComparison, setShowComparison] = useState(true);

  // Fetch project data
  const { data: project, isLoading: isLoadingCase } = useQuery<Case>({
    queryKey: ['case', caseId],
    queryFn: () => api.get<Case>(`/cases/${caseId}`),
    enabled: !!caseId,
  });

  // Fetch business domain
  const { data: businessDomain } = useQuery({
    queryKey: ['businessDomain', caseId],
    queryFn: async () => {
      try {
        return await api.get(`/cases/${caseId}/business-domain`);
      } catch (error: any) {
        if (error?.response?.status === 404) return null;
        throw error;
      }
    },
    enabled: !!caseId,
  });

  // Fetch company data
  const { data: companyData } = useQuery({
    queryKey: ['company', caseId],
    queryFn: async () => {
      try {
        return await api.get(`/company-research/cases/${caseId}/company`);
      } catch (error: any) {
        if (error?.response?.status === 404) return null;
        throw error;
      }
    },
    enabled: !!caseId,
  });

  // Fetch executive needs
  const { data: executiveNeeds } = useQuery({
    queryKey: ['executiveNeeds', caseId],
    queryFn: async () => {
      try {
        return await api.get(`/cases/${caseId}/executive-needs`);
      } catch (error: any) {
        if (error?.response?.status === 404) return null;
        throw error;
      }
    },
    enabled: !!caseId,
  });

  // Fetch data requirements
  const { data: dataRequirements } = useQuery({
    queryKey: ['dataRequirements', caseId],
    queryFn: async () => {
      try {
        return await api.get(`/data-requirements/cases/${caseId}/data-requirements`);
      } catch (error: any) {
        if (error?.response?.status === 404) return null;
        throw error;
      }
    },
    enabled: !!caseId,
  });

  // Fetch system landscape
  const { data: systemLandscape } = useQuery({
    queryKey: ['systemLandscape', caseId],
    queryFn: async () => {
      try {
        return await api.get(`/cases/${caseId}/system-landscape`);
      } catch (error: any) {
        if (error?.response?.status === 404) return null;
        throw error;
      }
    },
    enabled: !!caseId,
  });

  // Fetch all draft analyses
  const { data: allAnalyses, refetch: refetchAllAnalyses } = useQuery({
    queryKey: ['allDraftAnalyses', caseId],
    queryFn: async () => {
      try {
        const analyses = await api.get(`/draft-analysis/cases/${caseId}/analysis/all`);
        return analyses || [];
      } catch (error: any) {
        if (error?.response?.status === 404) return [];
        throw error;
      }
    },
    enabled: !!caseId,
    retry: false,
  });

  // Fetch draft analysis (specific or most recent)
  const { data: draftAnalysis, isLoading: isLoadingAnalysis, refetch: refetchAnalysis } = useQuery({
    queryKey: ['draftAnalysis', caseId, selectedAnalysisId],
    queryFn: async () => {
      try {
        if (selectedAnalysisId) {
          return await api.get(`/draft-analysis/cases/${caseId}/analysis/${selectedAnalysisId}`);
        } else {
          return await api.get(`/draft-analysis/cases/${caseId}/analysis`);
        }
      } catch (error: any) {
        if (error?.response?.status === 404) return null;
        throw error;
      }
    },
    enabled: !!caseId,
    retry: false,
  });

  // Mutation to start draft analysis
  const startAnalysisMutation = useMutation({
    mutationFn: async () => {
      return await api.post(`/draft-analysis/cases/${caseId}/analyze`, {});
    },
    onSuccess: () => {
      toast.success('Analysis started! This may take a few minutes...');
      setSelectedAnalysisId(null); // Reset to show most recent
      // Poll for results
      const interval = setInterval(async () => {
        const result = await refetchAnalysis();
        await refetchAllAnalyses();
        if (result.data && result.data.status === 'completed') {
          clearInterval(interval);
          toast.success('Analysis completed successfully!');
        } else if (result.data && result.data.status === 'failed') {
          clearInterval(interval);
          toast.error('Analysis failed. Please try again.');
        }
      }, 3000); // Poll every 3 seconds
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to start analysis');
    },
  });

  // Mutation to cancel in-progress analysis
  const cancelAnalysisMutation = useMutation({
    mutationFn: async (analysisId: string) => {
      return await api.post(`/draft-analysis/cases/${caseId}/analysis/${analysisId}/cancel`, {});
    },
    onSuccess: () => {
      toast.success('Analysis cancelled successfully');
      refetchAnalysis();
      refetchAllAnalyses();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to cancel analysis');
    },
  });

  // Mutation to delete specific analysis
  const deleteAnalysisMutation = useMutation({
    mutationFn: async (analysisId: string) => {
      return await api.delete(`/draft-analysis/cases/${caseId}/analysis/${analysisId}`);
    },
    onSuccess: () => {
      toast.success('Analysis version deleted successfully');
      setSelectedAnalysisId(null); // Reset to show most recent
      refetchAnalysis();
      refetchAllAnalyses();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to delete analysis');
    },
  });

  // Mutation to move to Processing phase
  const startProcessingMutation = useMutation({
    mutationFn: async () => {
      return await api.patch(`/cases/${caseId}`, {
        status: 'Processing'
      });
    },
    onSuccess: () => {
      toast.success('Draft phase completed! Project moved to Processing phase.');
      // TODO: Navigate to processing phase page (with Data Files upload as first step)
      navigate(`/cases/${caseId}/processing`);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to start processing phase');
    },
  });

  // Mutation to rewrite business context with AI (shows inline review)
  const rewriteBusinessContextMutation = useMutation({
    mutationFn: async () => {
      return await api.post(`/cases/${caseId}/business-domain/rewrite`, {});
    },
    onSuccess: (data: any) => {
      // Store AI-rewritten data and show inline review UI
      setAiRewrittenData({
        domain: data.domain,
        context: data.context
      });
      setEditedDomain(data.domain);
      setEditedContext(data.context);
      setShowRewriteReview(true);
      setShowComparison(true);
      // Expand the business domain section
      setExpandedSections(prev => new Set([...prev, 'domain']));
      toast.success('AI rewrite complete! Review and edit before saving.');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to rewrite business context');
    },
  });

  // Mutation to save reviewed business domain to Neo4j
  const saveBusinessDomainMutation = useMutation({
    mutationFn: async ({ domain, context }: { domain: string; context: string }) => {
      return await api.patch(`/cases/${caseId}/business-domain`, {
        domain,
        context
      });
    },
    onSuccess: () => {
      toast.success('Business domain updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['businessDomain', caseId] });
      setShowRewriteReview(false);
      setAiRewrittenData(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to save business domain');
    },
  });

  const handleCancelRewrite = () => {
    setShowRewriteReview(false);
    setAiRewrittenData(null);
    setEditedDomain('');
    setEditedContext('');
  };

  const handleResetDomain = () => {
    setEditedDomain(businessDomain?.domain || '');
  };

  const handleResetContext = () => {
    setEditedContext(businessDomain?.context || '');
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const getTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  if (isLoadingCase) {
    return (
      <div className="max-w-screen-2xl mx-auto flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(`/cases/${caseId}/edit`)}
          className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Project Setup
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-[40px] font-bold text-neutral-900 mb-2">
              Draft Phase Summary
            </h1>
            <p className="text-neutral-600">
              Review all collected information before starting Business Transformation Studio analysis and processing
            </p>
          </div>
          {project?.companyId && (
            <Button
              variant="outline"
              onClick={() => navigate(`/cases/${caseId}/client-details`)}
            >
              <Building2 className="w-4 h-4" />
              View Client Details
            </Button>
          )}
        </div>
      </div>

      {/* Executive Needs Analysis - Before First Run */}
      {!draftAnalysis && !isLoadingAnalysis && (
        <Card className="mb-6 border-l-4 border-l-blue-500">
          <CardBody>
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Sparkles className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                  Executive Needs Analysis
                </h3>
                <p className="text-neutral-600 mb-4">
                  Run Business Transformation Studio analysis to review each executive need and identify:
                </p>
                <ul className="space-y-2 text-sm text-neutral-700 mb-4">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>What questions can be answered with available data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span>What data gaps exist and why they prevent answering questions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>Where data is partially available but incomplete</span>
                  </li>
                </ul>
                <Button
                  onClick={() => startAnalysisMutation.mutate()}
                  loading={startAnalysisMutation.isPending}
                  disabled={startAnalysisMutation.isPending}
                >
                  <PlayCircle className="w-4 h-4" />
                  Start Analysis
                </Button>
                {(!executiveNeeds || !executiveNeeds.stakeholders || executiveNeeds.stakeholders.length === 0) && (
                  <p className="text-sm text-amber-600 mt-2">
                    Note: No executive needs found. Add executive needs before running analysis.
                  </p>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {isLoadingAnalysis && (
        <Card className="mb-6 border-l-4 border-l-blue-500">
          <CardBody>
            <div className="flex items-center gap-4">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-1">
                  Analysis in Progress...
                </h3>
                <p className="text-sm text-neutral-600">
                  Business Transformation Studio is analyzing your executive needs. This may take a few minutes.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {draftAnalysis && draftAnalysis.status === 'in_progress' && (
        <Card className="mb-6 border-l-4 border-l-amber-500">
          <CardBody>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Loader2 className="w-6 h-6 text-amber-600 animate-spin" />
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-1">
                    Analysis in Progress...
                  </h3>
                  <p className="text-sm text-neutral-600">
                    Business Transformation Studio is analyzing your executive needs. This may take a few minutes.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => cancelAnalysisMutation.mutate(draftAnalysis.analysisId)}
                loading={cancelAnalysisMutation.isPending}
                disabled={cancelAnalysisMutation.isPending}
                className="text-red-600 hover:bg-red-50 border-red-300"
              >
                <XCircle className="w-4 h-4" />
                Cancel
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Cancelled Analysis State - Show observations if any */}
      {draftAnalysis && draftAnalysis.status === 'cancelled' && draftAnalysis.observations && draftAnalysis.observations.length > 0 && (
        <div className="mb-6 space-y-4">
          <Card className="border-l-4 border-l-gray-500">
            <CardBody>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <XCircle className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-neutral-900">Analysis Cancelled (Partial Results)</h2>
                    <p className="text-sm text-neutral-600">
                      Analyzed {draftAnalysis.observations.length} need{draftAnalysis.observations.length !== 1 ? 's' : ''} before cancellation
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {allAnalyses && allAnalyses.length > 1 && (
                    <div className="relative">
                      <Button
                        variant="outline"
                        onClick={() => setShowHistoryDropdown(!showHistoryDropdown)}
                      >
                        <History className="w-4 h-4" />
                        Version History ({allAnalyses.length})
                      </Button>
                      {showHistoryDropdown && (
                        <div className="absolute right-0 mt-2 w-96 bg-white border border-neutral-200 rounded-lg shadow-lg z-10 max-h-96 overflow-y-auto">
                          <div className="p-3 border-b border-neutral-200">
                            <h3 className="text-sm font-semibold text-neutral-900">Analysis History</h3>
                            <p className="text-xs text-neutral-600 mt-1">Select a version to view or delete old analyses</p>
                          </div>
                          <div className="p-2">
                            {allAnalyses.map((analysis: any, idx: number) => {
                              const isSelected = selectedAnalysisId === analysis.analysisId || (!selectedAnalysisId && idx === 0);
                              const date = new Date(analysis.createdAt);
                              const timeAgo = getTimeAgo(date);
                              return (
                                <div
                                  key={analysis.analysisId}
                                  className={`p-3 rounded-lg mb-2 border ${
                                    isSelected
                                      ? 'bg-primary-50 border-primary-200'
                                      : 'bg-neutral-50 border-neutral-200 hover:bg-neutral-100'
                                  } cursor-pointer transition-colors`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div
                                      className="flex-1"
                                      onClick={() => {
                                        setSelectedAnalysisId(analysis.analysisId);
                                        setShowHistoryDropdown(false);
                                      }}
                                    >
                                      <div className="flex items-center gap-2 mb-1">
                                        <Clock className="w-3 h-3 text-neutral-600" />
                                        <span className="text-sm font-medium text-neutral-900">
                                          {date.toLocaleString()}
                                        </span>
                                        {idx === 0 && !selectedAnalysisId && (
                                          <span className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full font-medium">
                                            Current
                                          </span>
                                        )}
                                        {analysis.status === 'cancelled' && (
                                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full font-medium">
                                            Cancelled
                                          </span>
                                        )}
                                        {analysis.status === 'completed' && (
                                          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">
                                            Completed
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-xs text-neutral-600">{timeAgo}</p>
                                      <p className="text-xs text-neutral-600 mt-1">
                                        {analysis.observations?.length || 0} needs analyzed
                                      </p>
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setDeleteConfirmation({
                                          show: true,
                                          analysisId: analysis.analysisId,
                                          position: {
                                            top: rect.top + rect.height / 2,
                                            left: rect.left - 280,
                                          },
                                        });
                                      }}
                                      className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                      title="Delete this version"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => startAnalysisMutation.mutate()}
                    loading={startAnalysisMutation.isPending}
                    disabled={startAnalysisMutation.isPending}
                  >
                    <PlayCircle className="w-4 h-4" />
                    Re-run Analysis
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Show the same observations display as completed analysis */}
          {(() => {
            const groupedByStakeholder: Record<string, any[]> = {};
            draftAnalysis.observations.forEach((obs: any) => {
              const key = `${obs.stakeholderTitle || 'Unknown'} (${obs.stakeholderRole || 'Unknown'})`;
              if (!groupedByStakeholder[key]) {
                groupedByStakeholder[key] = [];
              }
              groupedByStakeholder[key].push(obs);
            });

            return Object.entries(groupedByStakeholder).map(([stakeholder, needs]) => (
              <div key={stakeholder} className="space-y-3">
                <div className="flex items-center gap-2 mt-4">
                  <Users className="w-5 h-5 text-primary-600" />
                  <h3 className="text-lg font-bold text-neutral-900">{stakeholder}</h3>
                  <span className="text-sm text-neutral-600">({needs.length} question{needs.length !== 1 ? 's' : ''})</span>
                </div>

                {needs.map((needAnalysis: any) => (
                  <Card key={needAnalysis.needId} className="border-l-4 border-l-purple-500">
                    <CardHeader>
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Sparkles className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-neutral-900 mb-1">
                            {needAnalysis.question}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-neutral-600">
                            <span className="flex items-center gap-1">
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                              {needAnalysis.answerable?.length || 0} Answerable
                            </span>
                            <span className="flex items-center gap-1">
                              <XCircle className="w-4 h-4 text-red-600" />
                              {needAnalysis.gaps?.length || 0} Gaps
                            </span>
                            <span className="flex items-center gap-1">
                              <AlertCircle className="w-4 h-4 text-amber-600" />
                              {needAnalysis.partial?.length || 0} Partial
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardBody>
                {/* Answerable Aspects */}
                {needAnalysis.answerable && needAnalysis.answerable.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Can Be Answered ({needAnalysis.answerable.length})
                    </h4>
                    <div className="space-y-3">
                      {needAnalysis.answerable.map((obs: any, idx: number) => (
                        <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <p className="font-medium text-neutral-900 mb-2">{obs.title}</p>
                          <p className="text-sm text-neutral-700 mb-3">{obs.description}</p>
                          {obs.supportingData && obs.supportingData.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-neutral-700 mb-1">Supporting Data:</p>
                              <div className="flex flex-wrap gap-1">
                                {obs.supportingData.map((data: string, i: number) => (
                                  <span key={i} className="text-xs px-2 py-1 bg-white rounded border border-green-300 text-green-800">
                                    {data}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {obs.supportingSystems && obs.supportingSystems.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-neutral-700 mb-1">Systems:</p>
                              <div className="flex flex-wrap gap-1">
                                {obs.supportingSystems.map((system: string, i: number) => (
                                  <span key={i} className="text-xs px-2 py-1 bg-white rounded border border-blue-300 text-blue-800">
                                    {system}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {obs.recommendation && (
                            <p className="text-xs italic text-neutral-600 mt-2">
                              💡 {obs.recommendation}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Data Gaps */}
                {needAnalysis.gaps && needAnalysis.gaps.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-red-800 mb-3 flex items-center gap-2">
                      <XCircle className="w-4 h-4" />
                      Data Gaps ({needAnalysis.gaps.length})
                    </h4>
                    <div className="space-y-3">
                      {needAnalysis.gaps.map((obs: any, idx: number) => (
                        <div key={idx} className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <p className="font-medium text-neutral-900">{obs.title}</p>
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              obs.priority === 'critical' ? 'bg-red-200 text-red-800' :
                              obs.priority === 'high' ? 'bg-orange-200 text-orange-800' :
                              obs.priority === 'medium' ? 'bg-amber-200 text-amber-800' :
                              'bg-gray-200 text-gray-800'
                            }`}>
                              {obs.priority} priority
                            </span>
                          </div>
                          <p className="text-sm text-neutral-700 mb-3">{obs.description}</p>
                          {obs.missingData && obs.missingData.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-neutral-700 mb-1">Missing Data:</p>
                              <div className="flex flex-wrap gap-1">
                                {obs.missingData.map((data: string, i: number) => (
                                  <span key={i} className="text-xs px-2 py-1 bg-white rounded border border-red-300 text-red-800">
                                    {data}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {obs.impact && (
                            <p className="text-xs text-neutral-600 mb-2">
                              <strong>Impact:</strong> {obs.impact}
                            </p>
                          )}
                          {obs.potentialSources && obs.potentialSources.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-neutral-700 mb-1">Potential Sources:</p>
                              <div className="flex flex-wrap gap-1">
                                {obs.potentialSources.map((source: string, i: number) => (
                                  <span key={i} className="text-xs px-2 py-1 bg-white rounded border border-purple-300 text-purple-800">
                                    {source}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {obs.recommendation && (
                            <p className="text-xs italic text-neutral-600 mt-2">
                              💡 {obs.recommendation}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Partial Coverage */}
                {needAnalysis.partial && needAnalysis.partial.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Partially Available ({needAnalysis.partial.length})
                    </h4>
                    <div className="space-y-3">
                      {needAnalysis.partial.map((obs: any, idx: number) => (
                        <div key={idx} className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <p className="font-medium text-neutral-900 mb-2">{obs.title}</p>
                          <p className="text-sm text-neutral-700 mb-3">{obs.description}</p>
                          {obs.availableData && obs.availableData.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-neutral-700 mb-1">Available Data:</p>
                              <div className="flex flex-wrap gap-1">
                                {obs.availableData.map((data: string, i: number) => (
                                  <span key={i} className="text-xs px-2 py-1 bg-white rounded border border-amber-300 text-amber-800">
                                    {data}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {obs.limitations && obs.limitations.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-neutral-700 mb-1">Limitations:</p>
                              <ul className="text-xs text-neutral-600 list-disc list-inside">
                                {obs.limitations.map((limitation: string, i: number) => (
                                  <li key={i}>{limitation}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {obs.recommendation && (
                            <p className="text-xs italic text-neutral-600 mt-2">
                              💡 {obs.recommendation}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                    </CardBody>
                  </Card>
                ))}
              </div>
            ));
          })()}
        </div>
      )}

      {/* Cancelled Analysis State - No observations */}
      {draftAnalysis && draftAnalysis.status === 'cancelled' && (!draftAnalysis.observations || draftAnalysis.observations.length === 0) && (
        <Card className="mb-6 border-l-4 border-l-gray-500">
          <CardBody>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-gray-100 rounded-lg">
                  <XCircle className="w-6 h-6 text-gray-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    Analysis Cancelled
                  </h3>
                  <p className="text-neutral-600 mb-4">
                    The analysis was cancelled before any results were generated. You can start a new analysis anytime.
                  </p>
                  <Button
                    onClick={() => startAnalysisMutation.mutate()}
                    loading={startAnalysisMutation.isPending}
                    disabled={startAnalysisMutation.isPending}
                  >
                    <PlayCircle className="w-4 h-4" />
                    Start New Analysis
                  </Button>
                </div>
              </div>
              {allAnalyses && allAnalyses.length > 1 && (
                <div className="relative">
                  <Button
                    variant="outline"
                    onClick={() => setShowHistoryDropdown(!showHistoryDropdown)}
                  >
                    <History className="w-4 h-4" />
                    Version History ({allAnalyses.length})
                  </Button>
                  {showHistoryDropdown && (
                    <div className="absolute right-0 mt-2 w-96 bg-white border border-neutral-200 rounded-lg shadow-lg z-10 max-h-96 overflow-y-auto">
                      <div className="p-3 border-b border-neutral-200">
                        <h3 className="text-sm font-semibold text-neutral-900">Analysis History</h3>
                        <p className="text-xs text-neutral-600 mt-1">Select a version to view or delete old analyses</p>
                      </div>
                      <div className="p-2">
                        {allAnalyses.map((analysis: any, idx: number) => {
                          const isSelected = selectedAnalysisId === analysis.analysisId || (!selectedAnalysisId && idx === 0);
                          const date = new Date(analysis.createdAt);
                          const timeAgo = getTimeAgo(date);
                          return (
                            <div
                              key={analysis.analysisId}
                              className={`p-3 rounded-lg mb-2 border ${
                                isSelected
                                  ? 'bg-primary-50 border-primary-200'
                                  : 'bg-neutral-50 border-neutral-200 hover:bg-neutral-100'
                              } cursor-pointer transition-colors`}
                            >
                              <div className="flex items-start justify-between">
                                <div
                                  className="flex-1"
                                  onClick={() => {
                                    setSelectedAnalysisId(analysis.analysisId);
                                    setShowHistoryDropdown(false);
                                  }}
                                >
                                  <div className="flex items-center gap-2 mb-1">
                                    <Clock className="w-3 h-3 text-neutral-600" />
                                    <span className="text-sm font-medium text-neutral-900">
                                      {date.toLocaleString()}
                                    </span>
                                    {idx === 0 && !selectedAnalysisId && (
                                      <span className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full font-medium">
                                        Current
                                      </span>
                                    )}
                                    {analysis.status === 'cancelled' && (
                                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full font-medium">
                                        Cancelled
                                      </span>
                                    )}
                                    {analysis.status === 'completed' && (
                                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">
                                        Completed
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-neutral-600">{timeAgo}</p>
                                  <p className="text-xs text-neutral-600 mt-1">
                                    {analysis.observations?.length || 0} needs analyzed
                                  </p>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setDeleteConfirmation({
                                      show: true,
                                      analysisId: analysis.analysisId,
                                      position: {
                                        top: rect.top + rect.height / 2,
                                        left: rect.left - 280,
                                      },
                                    });
                                  }}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Delete this version"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Failed Analysis State */}
      {draftAnalysis && draftAnalysis.status === 'failed' && (
        <Card className="mb-6 border-l-4 border-l-red-500">
          <CardBody>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-red-100 rounded-lg">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    Analysis Failed
                  </h3>
                  <p className="text-neutral-600 mb-4">
                    The analysis encountered an error. Please try running the analysis again.
                  </p>
                  <Button
                    onClick={() => startAnalysisMutation.mutate()}
                    loading={startAnalysisMutation.isPending}
                    disabled={startAnalysisMutation.isPending}
                  >
                    <PlayCircle className="w-4 h-4" />
                    Re-run Analysis
                  </Button>
                </div>
              </div>
              {allAnalyses && allAnalyses.length > 1 && (
                <div className="relative">
                  <Button
                    variant="outline"
                    onClick={() => setShowHistoryDropdown(!showHistoryDropdown)}
                  >
                    <History className="w-4 h-4" />
                    Version History ({allAnalyses.length})
                  </Button>
                  {showHistoryDropdown && (
                    <div className="absolute right-0 mt-2 w-96 bg-white border border-neutral-200 rounded-lg shadow-lg z-10 max-h-96 overflow-y-auto">
                      <div className="p-3 border-b border-neutral-200">
                        <h3 className="text-sm font-semibold text-neutral-900">Analysis History</h3>
                        <p className="text-xs text-neutral-600 mt-1">Select a version to view or delete old analyses</p>
                      </div>
                      <div className="p-2">
                        {allAnalyses.map((analysis: any, idx: number) => {
                          const isSelected = selectedAnalysisId === analysis.analysisId || (!selectedAnalysisId && idx === 0);
                          const date = new Date(analysis.createdAt);
                          const timeAgo = getTimeAgo(date);
                          return (
                            <div
                              key={analysis.analysisId}
                              className={`p-3 rounded-lg mb-2 border ${
                                isSelected
                                  ? 'bg-primary-50 border-primary-200'
                                  : 'bg-neutral-50 border-neutral-200 hover:bg-neutral-100'
                              } cursor-pointer transition-colors`}
                            >
                              <div className="flex items-start justify-between">
                                <div
                                  className="flex-1"
                                  onClick={() => {
                                    setSelectedAnalysisId(analysis.analysisId);
                                    setShowHistoryDropdown(false);
                                  }}
                                >
                                  <div className="flex items-center gap-2 mb-1">
                                    <Clock className="w-3 h-3 text-neutral-600" />
                                    <span className="text-sm font-medium text-neutral-900">
                                      {date.toLocaleString()}
                                    </span>
                                    {idx === 0 && !selectedAnalysisId && (
                                      <span className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full font-medium">
                                        Current
                                      </span>
                                    )}
                                    {analysis.status === 'cancelled' && (
                                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full font-medium">
                                        Cancelled
                                      </span>
                                    )}
                                    {analysis.status === 'completed' && (
                                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">
                                        Completed
                                      </span>
                                    )}
                                    {analysis.status === 'failed' && (
                                      <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">
                                        Failed
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-neutral-600">{timeAgo}</p>
                                  <p className="text-xs text-neutral-600 mt-1">
                                    {analysis.observations?.length || 0} needs analyzed
                                  </p>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setDeleteConfirmation({
                                      show: true,
                                      analysisId: analysis.analysisId,
                                      position: {
                                        top: rect.top + rect.height / 2,
                                        left: rect.left - 280,
                                      },
                                    });
                                  }}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Delete this version"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {draftAnalysis && draftAnalysis.status === 'completed' && draftAnalysis.observations && (
        <div className="mb-6 space-y-4">
          <Card className="border-l-4 border-l-green-500">
            <CardHeader>
              <button
                onClick={() => toggleSection('analysis')}
                className="flex items-center justify-between w-full"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-neutral-900">Executive Needs Analysis Complete</h2>
                    <p className="text-sm text-neutral-600">
                      Analyzed {draftAnalysis.observations.length} executive need{draftAnalysis.observations.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                  {allAnalyses && allAnalyses.length > 1 && (
                    <div className="relative">
                      <Button
                        variant="outline"
                        onClick={() => setShowHistoryDropdown(!showHistoryDropdown)}
                      >
                        <History className="w-4 h-4" />
                        Version History ({allAnalyses.length})
                      </Button>
                      {showHistoryDropdown && (
                        <div className="absolute right-0 mt-2 w-96 bg-white border border-neutral-200 rounded-lg shadow-lg z-10 max-h-96 overflow-y-auto">
                          <div className="p-3 border-b border-neutral-200">
                            <h3 className="text-sm font-semibold text-neutral-900">Analysis History</h3>
                            <p className="text-xs text-neutral-600 mt-1">Select a version to view or delete old analyses</p>
                          </div>
                          <div className="p-2">
                            {allAnalyses.map((analysis: any, idx: number) => {
                              const isSelected = selectedAnalysisId === analysis.analysisId || (!selectedAnalysisId && idx === 0);
                              const date = new Date(analysis.createdAt);
                              const timeAgo = getTimeAgo(date);
                              return (
                                <div
                                  key={analysis.analysisId}
                                  className={`p-3 rounded-lg mb-2 border ${
                                    isSelected
                                      ? 'bg-primary-50 border-primary-200'
                                      : 'bg-neutral-50 border-neutral-200 hover:bg-neutral-100'
                                  } cursor-pointer transition-colors`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div
                                      className="flex-1"
                                      onClick={() => {
                                        setSelectedAnalysisId(analysis.analysisId);
                                        setShowHistoryDropdown(false);
                                      }}
                                    >
                                      <div className="flex items-center gap-2 mb-1">
                                        <Clock className="w-3 h-3 text-neutral-600" />
                                        <span className="text-sm font-medium text-neutral-900">
                                          {date.toLocaleString()}
                                        </span>
                                        {idx === 0 && !selectedAnalysisId && (
                                          <span className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full font-medium">
                                            Current
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-xs text-neutral-600">{timeAgo}</p>
                                      <p className="text-xs text-neutral-600 mt-1">
                                        {analysis.observations?.length || 0} needs analyzed
                                      </p>
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setDeleteConfirmation({
                                          show: true,
                                          analysisId: analysis.analysisId,
                                          position: {
                                            top: rect.top + rect.height / 2,
                                            left: rect.left - 280, // Position to the left of the button
                                          },
                                        });
                                      }}
                                      className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                      title="Delete this version"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <Button
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      startAnalysisMutation.mutate();
                    }}
                    loading={startAnalysisMutation.isPending}
                    disabled={startAnalysisMutation.isPending}
                  >
                    <PlayCircle className="w-4 h-4" />
                    Re-run Analysis
                  </Button>
                  </div>
                  {expandedSections.has('analysis') ? (
                    <ChevronDown className="w-5 h-5 text-neutral-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-neutral-500" />
                  )}
                </div>
              </button>
            </CardHeader>
          </Card>

          {expandedSections.has('analysis') && (() => {
            const groupedByStakeholder: Record<string, any[]> = {};
            draftAnalysis.observations.forEach((obs: any) => {
              const key = `${obs.stakeholderTitle || 'Unknown'} (${obs.stakeholderRole || 'Unknown'})`;
              if (!groupedByStakeholder[key]) {
                groupedByStakeholder[key] = [];
              }
              groupedByStakeholder[key].push(obs);
            });

            return Object.entries(groupedByStakeholder).map(([stakeholder, needs]) => (
              <div key={stakeholder} className="space-y-3">
                {/* Stakeholder Header */}
                <div className="flex items-center gap-2 mt-4">
                  <Users className="w-5 h-5 text-primary-600" />
                  <h3 className="text-lg font-bold text-neutral-900">{stakeholder}</h3>
                  <span className="text-sm text-neutral-600">({needs.length} question{needs.length !== 1 ? 's' : ''})</span>
                </div>

                {/* Questions for this stakeholder */}
                {needs.map((needAnalysis: any) => (
                  <Card key={needAnalysis.needId} className="border-l-4 border-l-purple-500">
                    <CardHeader>
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Sparkles className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-neutral-900 mb-1">
                            {needAnalysis.question}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-neutral-600">
                            <span className="flex items-center gap-1">
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                              {needAnalysis.answerable?.length || 0} Answerable
                            </span>
                            <span className="flex items-center gap-1">
                              <XCircle className="w-4 h-4 text-red-600" />
                              {needAnalysis.gaps?.length || 0} Gaps
                            </span>
                            <span className="flex items-center gap-1">
                              <AlertCircle className="w-4 h-4 text-amber-600" />
                              {needAnalysis.partial?.length || 0} Partial
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
              <CardBody>
                {/* Answerable Aspects */}
                {needAnalysis.answerable && needAnalysis.answerable.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Can Be Answered ({needAnalysis.answerable.length})
                    </h4>
                    <div className="space-y-3">
                      {needAnalysis.answerable.map((obs: any, idx: number) => (
                        <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <p className="font-medium text-neutral-900 mb-2">{obs.title}</p>
                          <p className="text-sm text-neutral-700 mb-3">{obs.description}</p>
                          {obs.supportingData && obs.supportingData.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-neutral-700 mb-1">Supporting Data:</p>
                              <div className="flex flex-wrap gap-1">
                                {obs.supportingData.map((data: string, i: number) => (
                                  <span key={i} className="text-xs px-2 py-1 bg-white rounded border border-green-300 text-green-800">
                                    {data}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {obs.supportingSystems && obs.supportingSystems.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-neutral-700 mb-1">Systems:</p>
                              <div className="flex flex-wrap gap-1">
                                {obs.supportingSystems.map((system: string, i: number) => (
                                  <span key={i} className="text-xs px-2 py-1 bg-white rounded border border-blue-300 text-blue-800">
                                    {system}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {obs.recommendation && (
                            <p className="text-xs italic text-neutral-600 mt-2">
                              💡 {obs.recommendation}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Data Gaps */}
                {needAnalysis.gaps && needAnalysis.gaps.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-red-800 mb-3 flex items-center gap-2">
                      <XCircle className="w-4 h-4" />
                      Data Gaps ({needAnalysis.gaps.length})
                    </h4>
                    <div className="space-y-3">
                      {needAnalysis.gaps.map((obs: any, idx: number) => (
                        <div key={idx} className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <p className="font-medium text-neutral-900">{obs.title}</p>
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              obs.priority === 'critical' ? 'bg-red-200 text-red-800' :
                              obs.priority === 'high' ? 'bg-orange-200 text-orange-800' :
                              obs.priority === 'medium' ? 'bg-amber-200 text-amber-800' :
                              'bg-gray-200 text-gray-800'
                            }`}>
                              {obs.priority} priority
                            </span>
                          </div>
                          <p className="text-sm text-neutral-700 mb-3">{obs.description}</p>
                          {obs.missingData && obs.missingData.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-neutral-700 mb-1">Missing Data:</p>
                              <div className="flex flex-wrap gap-1">
                                {obs.missingData.map((data: string, i: number) => (
                                  <span key={i} className="text-xs px-2 py-1 bg-white rounded border border-red-300 text-red-800">
                                    {data}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {obs.impact && (
                            <p className="text-xs text-neutral-600 mb-2">
                              <strong>Impact:</strong> {obs.impact}
                            </p>
                          )}
                          {obs.potentialSources && obs.potentialSources.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-neutral-700 mb-1">Potential Sources:</p>
                              <div className="flex flex-wrap gap-1">
                                {obs.potentialSources.map((source: string, i: number) => (
                                  <span key={i} className="text-xs px-2 py-1 bg-white rounded border border-purple-300 text-purple-800">
                                    {source}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {obs.recommendation && (
                            <p className="text-xs italic text-neutral-600 mt-2">
                              💡 {obs.recommendation}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Partial Coverage */}
                {needAnalysis.partial && needAnalysis.partial.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Partially Available ({needAnalysis.partial.length})
                    </h4>
                    <div className="space-y-3">
                      {needAnalysis.partial.map((obs: any, idx: number) => (
                        <div key={idx} className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <p className="font-medium text-neutral-900 mb-2">{obs.title}</p>
                          <p className="text-sm text-neutral-700 mb-3">{obs.description}</p>
                          {obs.availableData && obs.availableData.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-neutral-700 mb-1">Available Data:</p>
                              <div className="flex flex-wrap gap-1">
                                {obs.availableData.map((data: string, i: number) => (
                                  <span key={i} className="text-xs px-2 py-1 bg-white rounded border border-amber-300 text-amber-800">
                                    {data}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {obs.limitations && obs.limitations.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-neutral-700 mb-1">Limitations:</p>
                              <ul className="text-xs text-neutral-600 list-disc list-inside">
                                {obs.limitations.map((limitation: string, i: number) => (
                                  <li key={i}>{limitation}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {obs.recommendation && (
                            <p className="text-xs italic text-neutral-600 mt-2">
                              💡 {obs.recommendation}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                    </CardBody>
                  </Card>
                ))}
              </div>
            ));
          })()}

          {/* AI Suggested Questions */}
          {draftAnalysis.suggestedQuestions && draftAnalysis.suggestedQuestions.length > 0 && (
            <Card className="border-l-4 border-l-blue-500 mt-6">
              <CardHeader>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Sparkles className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-neutral-900 mb-1">
                      Amplinth-Suggested Questions
                    </h3>
                    <p className="text-sm text-neutral-600">
                      Additional insights the platform can provide with your available data
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  {draftAnalysis.suggestedQuestions.map((suggestion: any, idx: number) => (
                    <div key={idx} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-semibold text-neutral-900 mb-1">{suggestion.question}</p>
                          <p className="text-sm text-neutral-700 mb-3">{suggestion.rationale}</p>
                        </div>
                        <span className={`ml-3 text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${
                          suggestion.confidence === 'high' ? 'bg-green-200 text-green-800' :
                          suggestion.confidence === 'medium' ? 'bg-amber-200 text-amber-800' :
                          'bg-gray-200 text-gray-800'
                        }`}>
                          {suggestion.confidence} confidence
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <p className="text-xs font-medium text-neutral-700 mb-1">Target Executive:</p>
                          <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded">
                            {suggestion.targetStakeholder}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-neutral-700 mb-1">Category:</p>
                          <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-800 rounded">
                            {suggestion.category}
                          </span>
                        </div>
                      </div>

                      {suggestion.requiredData && suggestion.requiredData.length > 0 && (
                        <div className="mb-2">
                          <p className="text-xs font-medium text-neutral-700 mb-1">Required Data:</p>
                          <div className="flex flex-wrap gap-1">
                            {suggestion.requiredData.map((data: string, i: number) => (
                              <span key={i} className="text-xs px-2 py-1 bg-white rounded border border-blue-300 text-blue-800">
                                {data}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {suggestion.availableSystems && suggestion.availableSystems.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-neutral-700 mb-1">Available in Systems:</p>
                          <div className="flex flex-wrap gap-1">
                            {suggestion.availableSystems.map((system: string, i: number) => (
                              <span key={i} className="text-xs px-2 py-1 bg-white rounded border border-green-300 text-green-800">
                                {system}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      )}

      {/* Summary Sections */}
      <div className="space-y-4">
        {/* Case Details */}
        <Card>
          <CardHeader>
            <button
              onClick={() => toggleSection('project')}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-neutral-900">Case Details</h3>
              </div>
              {expandedSections.has('project') ? (
                <ChevronDown className="w-5 h-5 text-neutral-500" />
              ) : (
                <ChevronRight className="w-5 h-5 text-neutral-500" />
              )}
            </button>
          </CardHeader>
          {expandedSections.has('project') && (
            <CardBody>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-neutral-700">Case Name</p>
                  <p className="text-neutral-900">{project?.name || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-neutral-700">Description</p>
                  <p className="text-neutral-900 whitespace-pre-wrap">{project?.description || 'Not specified'}</p>
                </div>
              </div>
            </CardBody>
          )}
        </Card>

        {/* Client Details */}
        {/* Business Domain */}
        {businessDomain && (
          <Card>
            <CardHeader>
              <button
                onClick={() => toggleSection('domain')}
                className="flex items-center justify-between w-full text-left"
              >
                <div className="flex items-center gap-3">
                  <Database className="w-5 h-5 text-primary-600" />
                  <h3 className="text-lg font-semibold text-neutral-900">Business Domain</h3>
                  {showRewriteReview && (
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                      Amplinth Review
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {!showRewriteReview && (
                    <Button
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        rewriteBusinessContextMutation.mutate();
                      }}
                      loading={rewriteBusinessContextMutation.isPending}
                      disabled={rewriteBusinessContextMutation.isPending}
                      className="text-sm"
                    >
                      <Sparkles className="w-4 h-4" />
                      Refine with Amplinth
                    </Button>
                  )}
                  {expandedSections.has('domain') ? (
                    <ChevronDown className="w-5 h-5 text-neutral-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-neutral-500" />
                  )}
                </div>
              </button>
            </CardHeader>
            {expandedSections.has('domain') && (
              <CardBody>
                {!showRewriteReview ? (
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-neutral-700">Domain</p>
                      <p className="text-neutral-900">{businessDomain.domain || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-neutral-700">Context</p>
                      <p className="text-neutral-900 whitespace-pre-wrap">{businessDomain.context || 'Not specified'}</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Toggle Comparison View */}
                    <div className="flex items-center justify-between pb-4 border-b border-neutral-200">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showComparison}
                          onChange={(e) => setShowComparison(e.target.checked)}
                          className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm font-medium text-neutral-700">
                          Show before/after comparison
                        </span>
                      </label>
                    </div>

                    {/* Business Domain Field */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-semibold text-neutral-900">
                          Business Domain
                        </label>
                        {editedDomain !== businessDomain?.domain && (
                          <Button
                            variant="outline"
                            onClick={handleResetDomain}
                            className="text-xs"
                          >
                            <RotateCcw className="w-3 h-3" />
                            Reset to Original
                          </Button>
                        )}
                      </div>

                      {showComparison && (
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <p className="text-xs font-medium text-neutral-600 mb-1">Before (Original)</p>
                            <div className="p-3 bg-neutral-50 border border-neutral-200 rounded-lg">
                              <p className="text-sm text-neutral-700">{businessDomain?.domain}</p>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-primary-600 mb-1">After (Refined)</p>
                            <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg">
                              <p className="text-sm text-neutral-900 font-medium">{aiRewrittenData?.domain || businessDomain?.domain}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <input
                        type="text"
                        value={editedDomain}
                        onChange={(e) => setEditedDomain(e.target.value)}
                        className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter business domain (e.g., Supply Chain Management)"
                      />
                      <p className="text-xs text-neutral-600 mt-1">
                        The business problem this project addresses
                      </p>
                    </div>

                    {/* Business Context Field */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-semibold text-neutral-900">
                          Business Context
                        </label>
                        {editedContext !== businessDomain?.context && (
                          <Button
                            variant="outline"
                            onClick={handleResetContext}
                            className="text-xs"
                          >
                            <RotateCcw className="w-3 h-3" />
                            Reset to Original
                          </Button>
                        )}
                      </div>

                      {showComparison && (
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <p className="text-xs font-medium text-neutral-600 mb-1">Before (Original)</p>
                            <div className="p-3 bg-neutral-50 border border-neutral-200 rounded-lg max-h-64 overflow-y-auto">
                              <p className="text-sm text-neutral-700 whitespace-pre-wrap">{businessDomain?.context}</p>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-primary-600 mb-1">After (Refined)</p>
                            <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg max-h-64 overflow-y-auto">
                              <p className="text-sm text-neutral-900 whitespace-pre-wrap">{aiRewrittenData?.context}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <textarea
                        value={editedContext}
                        onChange={(e) => setEditedContext(e.target.value)}
                        rows={12}
                        className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
                        placeholder="Describe the business context, challenges, and objectives..."
                      />
                      <p className="text-xs text-neutral-600 mt-1">
                        Comprehensive description of the business problem, data sources, and expected outcomes
                      </p>
                    </div>

                    {/* Improvements Notice */}
                    <Card className="bg-blue-50 border-blue-200">
                      <CardBody>
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <ArrowRight className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-neutral-900 mb-1">
                              What was improved:
                            </h4>
                            <ul className="text-sm text-neutral-700 space-y-1">
                              <li className="flex items-start gap-2">
                                <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                                <span>Grounded in actual company data (industry, products, financials)</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                                <span>Aligned with specific executive questions and business priorities</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                                <span>Referenced actual data entities and enterprise systems</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                                <span>Explained business value of graph relationships for this use case</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </CardBody>
                    </Card>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between pt-4 border-t border-neutral-200">
                      <Button
                        variant="outline"
                        onClick={handleCancelRewrite}
                        disabled={saveBusinessDomainMutation.isPending}
                      >
                        Cancel
                      </Button>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditedDomain(aiRewrittenData?.domain || businessDomain?.domain || '');
                            setEditedContext(aiRewrittenData?.context || businessDomain?.context || '');
                          }}
                          disabled={saveBusinessDomainMutation.isPending}
                        >
                          <RotateCcw className="w-4 h-4" />
                          Reset All
                        </Button>
                        <Button
                          onClick={() => saveBusinessDomainMutation.mutate({ domain: editedDomain, context: editedContext })}
                          loading={saveBusinessDomainMutation.isPending}
                          disabled={saveBusinessDomainMutation.isPending || !editedDomain.trim() || !editedContext.trim()}
                        >
                          <Check className="w-4 h-4" />
                          Save to Neo4j
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardBody>
            )}
          </Card>
        )}

        {companyData && (
          <Card>
            <CardHeader>
              <button
                onClick={() => toggleSection('client')}
                className="flex items-center justify-between w-full text-left"
              >
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-primary-600" />
                  <h3 className="text-lg font-semibold text-neutral-900">Client Information</h3>
                </div>
                {expandedSections.has('client') ? (
                  <ChevronDown className="w-5 h-5 text-neutral-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-neutral-500" />
                )}
              </button>
            </CardHeader>
            {expandedSections.has('client') && (
              <CardBody>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-neutral-700">Company Name</p>
                    <p className="text-neutral-900">{companyData.company?.name || 'Not specified'}</p>
                  </div>
                  {companyData.company?.ticker && (
                    <div>
                      <p className="text-sm font-medium text-neutral-700">Ticker Symbol</p>
                      <p className="text-neutral-900">{companyData.company.ticker}</p>
                    </div>
                  )}
                  {companyData.company?.websiteUrl && (
                    <div>
                      <p className="text-sm font-medium text-neutral-700">Website</p>
                      <a
                        href={companyData.company.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:underline"
                      >
                        {companyData.company.websiteUrl}
                      </a>
                    </div>
                  )}
                </div>
              </CardBody>
            )}
          </Card>
        )}

        {/* Executive Needs */}
        {executiveNeeds && executiveNeeds.needs && executiveNeeds.needs.length > 0 && (
          <Card>
            <CardHeader>
              <button
                onClick={() => toggleSection('executive')}
                className="flex items-center justify-between w-full text-left"
              >
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-primary-600" />
                  <h3 className="text-lg font-semibold text-neutral-900">
                    Executive Needs ({executiveNeeds.needs.length})
                  </h3>
                </div>
                {expandedSections.has('executive') ? (
                  <ChevronDown className="w-5 h-5 text-neutral-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-neutral-500" />
                )}
              </button>
            </CardHeader>
            {expandedSections.has('executive') && (
              <CardBody>
                <div className="space-y-4">
                  {executiveNeeds.needs.map((need: any, idx: number) => (
                    <div key={idx} className="p-3 bg-neutral-50 rounded-lg">
                      <p className="font-medium text-neutral-900 mb-1">{need.question}</p>
                      <p className="text-sm text-neutral-600">{need.stakeholder} • {need.priority} priority</p>
                    </div>
                  ))}
                </div>
              </CardBody>
            )}
          </Card>
        )}

        {/* Data Requirements Summary */}
        {dataRequirements && (
          <Card>
            <CardHeader>
              <button
                onClick={() => toggleSection('data')}
                className="flex items-center justify-between w-full text-left"
              >
                <div className="flex items-center gap-3">
                  <Database className="w-5 h-5 text-primary-600" />
                  <h3 className="text-lg font-semibold text-neutral-900">
                    Data Requirements (
                    {(dataRequirements.masterData?.length || 0) + (dataRequirements.transactionalData?.length || 0)} entities)
                  </h3>
                </div>
                {expandedSections.has('data') ? (
                  <ChevronDown className="w-5 h-5 text-neutral-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-neutral-500" />
                )}
              </button>
            </CardHeader>
            {expandedSections.has('data') && (
              <CardBody>
                <div className="space-y-4">
                  {dataRequirements.masterData && dataRequirements.masterData.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-neutral-700 mb-2">
                        Master Data ({dataRequirements.masterData.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {dataRequirements.masterData.map((item: any, idx: number) => (
                          <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                            {item.entity}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {dataRequirements.transactionalData && dataRequirements.transactionalData.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-neutral-700 mb-2">
                        Transactional Data ({dataRequirements.transactionalData.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {dataRequirements.transactionalData.map((item: any, idx: number) => (
                          <span key={idx} className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                            {item.entity}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardBody>
            )}
          </Card>
        )}

        {/* System Landscape */}
        {systemLandscape && systemLandscape.systems && systemLandscape.systems.length > 0 && (
          <Card>
            <CardHeader>
              <button
                onClick={() => toggleSection('systems')}
                className="flex items-center justify-between w-full text-left"
              >
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-primary-600" />
                  <h3 className="text-lg font-semibold text-neutral-900">
                    System Landscape ({systemLandscape.systems.length} systems)
                  </h3>
                </div>
                {expandedSections.has('systems') ? (
                  <ChevronDown className="w-5 h-5 text-neutral-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-neutral-500" />
                )}
              </button>
            </CardHeader>
            {expandedSections.has('systems') && (
              <CardBody>
                <div className="space-y-3">
                  {systemLandscape.systems.map((system: any, idx: number) => (
                    <div key={idx} className="p-3 bg-neutral-50 rounded-lg">
                      <p className="font-medium text-neutral-900">{system.name}</p>
                      <p className="text-sm text-neutral-600 mt-1">{system.businessUse}</p>
                    </div>
                  ))}
                </div>
              </CardBody>
            )}
          </Card>
        )}
      </div>

      {/* Actions */}
      <Card className="mt-6">
        <CardFooter>
          <div className="flex items-center justify-between w-full">
            <Button
              variant="outline"
              onClick={() => navigate(`/cases/${caseId}/edit`)}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Edit
            </Button>
            <Button
              onClick={() => startProcessingMutation.mutate()}
              loading={startProcessingMutation.isPending}
              disabled={startProcessingMutation.isPending}
            >
              <Check className="w-4 h-4" />
              Complete Draft & Start Processing
            </Button>
          </div>
        </CardFooter>
      </Card>

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && deleteConfirmation.position && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-40"
            onClick={() => setDeleteConfirmation({ show: false, analysisId: null, position: null })}
          />
          {/* Modal */}
          <div
            className="fixed z-50 bg-white rounded-lg shadow-2xl border-2 border-red-200 p-5 w-72"
            style={{
              top: `${deleteConfirmation.position.top}px`,
              left: `${deleteConfirmation.position.left}px`,
              transform: 'translateY(-50%)',
            }}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-neutral-900 mb-1">Delete Analysis Version?</h3>
                <p className="text-sm text-neutral-600">
                  This action cannot be undone. The analysis version will be permanently deleted from Amplinth.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmation({ show: false, analysisId: null, position: null })}
                className="text-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (deleteConfirmation.analysisId) {
                    deleteAnalysisMutation.mutate(deleteConfirmation.analysisId);
                  }
                  setDeleteConfirmation({ show: false, analysisId: null, position: null });
                }}
                className="text-sm bg-red-600 hover:bg-red-700 text-white"
                loading={deleteAnalysisMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
